import { Alert, Button, Typography } from '@mui/material';
import type { ChatLimitsView, ChatUsageWindow } from '@hv/shared';

export type ChatBlock = { kind: 'turns' } | { kind: 'rate'; window: 'hour' | 'day'; usage: ChatUsageWindow } | null;

export function chatBlock(limits: ChatLimitsView | undefined): ChatBlock {
  if (!limits) return null;
  if (limits.turnsLeft === 0) return { kind: 'turns' };
  const exhausted = [
    { window: 'hour' as const, usage: limits.hourly },
    { window: 'day' as const, usage: limits.daily },
  ].filter((w) => w.usage.remaining === 0);
  if (exhausted.length === 0) return null;
  const last = exhausted.reduce((a, b) => ((b.usage.resetsAt ?? '') > (a.usage.resetsAt ?? '') ? b : a));
  return { kind: 'rate', ...last };
}

export function formatResetTime(iso: string, now = new Date()): string {
  const at = new Date(iso);
  const time = at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (at.toDateString() === now.toDateString()) return time;
  return `${at.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}, ${time}`;
}

const n = (v: number) => v.toLocaleString('en-US');
const questions = (v: number) => `${n(v)} question${v === 1 ? '' : 's'}`;

export function ChatLimitNotice({ limits, block, onNewChat, newChatPending }: { limits: ChatLimitsView; block: ChatBlock; onNewChat?: () => void; newChatPending?: boolean }) {
  if (block?.kind === 'turns') {
    return (
      <Alert
        severity="info"
        data-testid="chat-limit"
        action={
          onNewChat && (
            <Button color="inherit" size="small" onClick={onNewChat} disabled={newChatPending}>
              Start a new chat
            </Button>
          )
        }
      >
        This chat has reached its limit of {questions(limits.maxTurns)}. Start a new chat to keep asking.
      </Alert>
    );
  }
  if (block?.kind === 'rate') {
    const per = block.window === 'hour' ? 'per hour' : 'per day';
    return (
      <Alert severity="warning" data-testid="chat-limit">
        You've reached the limit of {questions(block.usage.limit)} {per}.
        {block.usage.resetsAt ? ` You can ask again at ${formatResetTime(block.usage.resetsAt)}.` : ''}
      </Alert>
    );
  }
  return (
    <Typography variant="caption" color="text.secondary" data-testid="chat-usage" sx={{ display: 'block' }}>
      {questions(limits.turnsLeft)} left in this chat · {n(limits.hourly.remaining)} left this hour · {n(limits.daily.remaining)} today
    </Typography>
  );
}
